// ABOUTME: Invite-gated sign-up form. Redeems the code and creates the account record,
// ABOUTME: then emails a passwordless sign-in link, so the user is in with one click.

import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { sanitizeEmail, sanitizeTitle } from "../utils/inputSanitization";
import { passwordlessAuthService } from "../services/firebase";
import { InviteError } from "../services/inviteService";
import { describeInviteProblem, isWellFormedCode, normalizeInviteCode } from "../utils/invites";
import { APP_NAME } from "../constants/branding";

export interface SignUpData {
  email: string;
  firstName: string;
  lastName: string;
  zip: string;
  ip?: string;
  inviteCode: string;
}

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Redeems the code and creates the account record. Rejects on failure (InviteError for a bad code). */
  onSubmit: (data: SignUpData) => Promise<void> | void;
  /** Pre-fills the code, e.g. from an invite link (?invite=CODE). */
  initialInviteCode?: string;
  /** Shown on the confirmation so a visitor can keep going before the email arrives. */
  onTryAsGuest?: () => void;
  /** Sends the sign-in link after the record is created. Defaults to the Firebase service; injectable for tests. */
  sendSignInLink?: (email: string) => Promise<unknown>;
}

/**
 * Best-effort client IP for the admin's anti-spam view. Fetched only on
 * submit — it used to run on mount, which pinged a third party for every
 * splash visitor who never opened the form.
 */
async function lookupIp(): Promise<string> {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const data = await res.json();
    return typeof data.ip === "string" ? data.ip : "";
  } catch {
    return "";
  }
}

const SignUpModal: React.FC<SignUpModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onTryAsGuest,
  initialInviteCode = "",
  sendSignInLink = passwordlessAuthService.sendSignInLink,
}) => {
  const [inviteCode, setInviteCode] = useState(initialInviteCode);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [zip, setZip] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  /** Set when the record saved but the email did not go out. */
  const [linkError, setLinkError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const code = normalizeInviteCode(inviteCode);
    if (!isWellFormedCode(code)) {
      setError("Please enter your invite code.");
      return;
    }
    const sanitizedEmail = sanitizeEmail(email);
    const sanitizedFirstName = sanitizeTitle(firstName, 50);
    const sanitizedLastName = sanitizeTitle(lastName, 50);
    const sanitizedZip = zip.trim();

    if (!sanitizedEmail) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!sanitizedFirstName || !sanitizedLastName) {
      setError("Please enter your first and last name.");
      return;
    }
    if (!/^\d{5}$/.test(sanitizedZip)) {
      setError("Please enter a 5-digit US ZIP code.");
      return;
    }

    setIsLoading(true);
    try {
      const ip = await lookupIp();
      await onSubmit({
        email: sanitizedEmail,
        firstName: sanitizedFirstName,
        lastName: sanitizedLastName,
        zip: sanitizedZip,
        ip,
        inviteCode: code,
      });
    } catch (err) {
      console.error("Sign-up failed:", err);
      setError(
        err instanceof InviteError
          ? describeInviteProblem(err.reason)
          : "We couldn't create your account. Please try again in a moment."
      );
      setIsLoading(false);
      return;
    }

    // The account exists; now get them in. A failure here is recoverable
    // from the Sign in dialog, so it is reported but does not undo anything.
    try {
      await sendSignInLink(sanitizedEmail);
      setLinkError("");
    } catch (err) {
      console.error("Sign-in link failed to send:", err);
      setLinkError(err instanceof Error ? err.message : "The sign-in email didn't send.");
    }
    setSubmittedEmail(sanitizedEmail);
    setIsLoading(false);
  };

  const handleClose = () => {
    onClose();
    // Reset after the transition so reopening starts clean.
    setTimeout(() => {
      setSubmittedEmail(null);
      setError("");
      setLinkError("");
    }, 300);
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Transition.Child
        as={React.Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-all z-40"
          aria-hidden="true"
        />
      </Transition.Child>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-[var(--radius-lg)] bg-[var(--color-bg-secondary)] p-6 border border-[var(--color-border)]">
            <Dialog.Title className="text-xl font-bold mb-1 text-[var(--color-text-primary)]">
              Create your account
            </Dialog.Title>
            {submittedEmail ? (
              <div className="text-center py-6">
                {linkError ? (
                  <>
                    <div className="text-lg font-semibold text-[var(--color-text-primary)] mb-2">
                      Account created
                    </div>
                    <p role="alert" className="text-sm text-[var(--color-danger)]">
                      We couldn't send the sign-in email to {submittedEmail}: {linkError}
                    </p>
                    <p className="text-sm text-[var(--color-text-tertiary)] mt-2">
                      Use <span className="font-medium">Sign in</span> to request another link.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-lg font-semibold text-[var(--color-accent)] mb-2">
                      Check your email
                    </div>
                    <p className="text-sm text-[var(--color-text-primary)]">
                      We sent a sign-in link to{" "}
                      <span className="font-medium">{submittedEmail}</span>. Open it in this browser
                      and you're in.
                    </p>
                  </>
                )}
                {onTryAsGuest && (
                  <p className="text-sm text-[var(--color-text-tertiary)] mt-3">
                    Waiting on the email? Use {APP_NAME} as a guest meanwhile — anything you add
                    here moves to your account when you sign in on this browser.
                  </p>
                )}
                <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                  {onTryAsGuest && (
                    <button type="button" className="btn btn-primary" onClick={onTryAsGuest}>
                      Try as guest
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm text-[var(--color-text-tertiary)] mb-4">
                  {APP_NAME} is invite-only while it's in beta. Enter the code you were given and
                  we'll email you a sign-in link right away — no password. US only for now.
                </p>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                  <input
                    type="text"
                    className="field font-mono uppercase tracking-wide"
                    placeholder="Invite code"
                    aria-label="Invite code"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    autoFocus={!initialInviteCode}
                  />
                  <input
                    type="email"
                    className="field"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    autoFocus={!!initialInviteCode}
                  />
                  <div className="flex flex-col gap-3 sm:flex-row sm:gap-2 w-full">
                    <input
                      type="text"
                      className="field w-full sm:w-1/2"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                    />
                    <input
                      type="text"
                      className="field w-full sm:w-1/2"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                    />
                  </div>
                  <input
                    type="text"
                    className="field"
                    placeholder="US ZIP code"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    pattern="\d{5}"
                    maxLength={5}
                    required
                  />
                  {error && (
                    <div role="alert" className="text-xs text-[var(--color-danger)]">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary mt-1 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating…" : "Create account"}
                  </button>
                </form>
                <div className="mt-4 text-center text-sm text-[var(--color-text-tertiary)]">
                  Already have an account?{" "}
                  <button
                    type="button"
                    className="text-[var(--color-accent)] hover:underline focus:outline-none"
                    onClick={() => {
                      handleClose();
                      window.dispatchEvent(new CustomEvent("open-sign-in-modal"));
                    }}
                  >
                    Sign in
                  </button>
                </div>
              </>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SignUpModal;
